import { UseFormReturn } from "react-hook-form";
import { Form } from "@themes/pollinator/ui/Form";
import { FormField } from "@themes/pollinator/ui/FormField";
import { ActionButtonsGroup } from "@themes/pollinator/ui/ActionButtonsGroup";
import type { TimezoneFormData } from "@core/features/profile";
import type { TimezoneOption } from "@core/features/timezones";

export type Props = {
  form: UseFormReturn<TimezoneFormData>;
  timezoneOptions: TimezoneOption[];
  isDirty: boolean;
  isValid: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  /** Custom primary button text */
  primaryButtonText?: string;
  /** Whether to show the cancel button (defaults to true when form is dirty) */
  showCancelButton?: boolean;
  /** Whether to show action buttons (defaults to true) */
  showActionButtons?: boolean;
  /** Size variant for the timezone select */
  size?: "default" | "hero";
  /** Label for the timezone field */
  label?: string;
  /** Layout variant for the form field */
  layout?: "vertical" | "horizontal";
};

/**
 * Presentational component for timezone form.
 * Allows users to select their timezone.
 */
export function TimezoneForm({
  form,
  timezoneOptions,
  isDirty,
  isValid,
  isSubmitting,
  onSubmit,
  onCancel,
  primaryButtonText = "Save",
  showCancelButton,
  showActionButtons = true,
  size = "default",
  label,
  layout,
}: Props) {
  const shouldShowCancel = showCancelButton ?? isDirty;
  return (
    <Form form={form} onSubmit={onSubmit}>
      <FormField
        name="timezone"
        label={label}
        component="TimezoneSelect"
        options={timezoneOptions}
        placeholder="Select your timezone"
        size={size}
        layout={layout}
        required
      />
      {showActionButtons && (
        <ActionButtonsGroup
          primaryButtonText={isSubmitting ? "Saving..." : primaryButtonText}
          onPrimaryClick={() => {
            if (!isDirty || isSubmitting) return;
            onSubmit();
          }}
          secondaryButtonText={shouldShowCancel ? "Cancel" : undefined}
          onSecondaryClick={shouldShowCancel ? onCancel : undefined}
          className="size-max w-fit self-end"
          primaryButtonProps={{
            visuallyDisabled: !isDirty || isSubmitting || !isValid,
          }}
        />
      )}
    </Form>
  );
}

export default TimezoneForm;
